/**
 * Change classification and description.
 */
import type { ChangeKind, Design } from './schemas';
import { CONSTRUCTION, type ConstructionProfile } from './profiles';
import { appearanceHashOf } from './build';
import { fmtMm, fmtNum } from './units';
import { familyOf } from '../families/registry';
import type { AnyStock } from '../families/stock';
import { bayCount, layoutOf } from './design';
import { contractOf } from '../families/contract';

const derive = (d: Design, stock?: AnyStock | null, construction: ConstructionProfile = CONSTRUCTION) => familyOf(d).generate(d, stock, construction);

/**
 * 'manufacturing' | 'appearance' | 'layout' | 'none'.
 * 'manufacturing' only when the cut list or datums change (the manufacturing hash differs).
 * 'layout' is a design setting that changes neither (today: the Even convenience);
 * it is a real, undoable edit that keeps the manufacturing hash. A change only the v2 contract sees
 * (supports, interfaces, fit history: its `assemblyHash`) is 'layout' too, so it is never dropped.
 */
export function classifyChange(prev: Design, next: Design, stock?: AnyStock | null, construction: ConstructionProfile = CONSTRUCTION): ChangeKind {
  const a = derive(prev, stock, construction), b = derive(next, stock, construction);
  if (a.manufacturingHash !== b.manufacturingHash) return 'manufacturing';
  if (appearanceHashOf(prev) !== appearanceHashOf(next)) return 'appearance';
  // A refused front may add no parts. Keep the edit so its error and offered fix remain visible and undoable.
  const fronts = (d: Design) => (d.params ? d.params.fronts : d.fronts) ?? [];
  if (JSON.stringify(fronts(prev)) !== JSON.stringify(fronts(next))) return 'layout';
  if (!!prev.even !== !!next.even) return 'layout';
  for (let i = 1; i < Math.max(bayCount(prev), bayCount(next)); i++) if (!!layoutOf(prev, i).even !== !!layoutOf(next, i).even) return 'layout';
  if (contractOf(a).assemblyHash !== contractOf(b).assemblyHash) return 'layout';
  return 'none';
}

/** Human-readable list of manufacturing differences, e.g. ["Shelf 2 moved up 20 mm"], ["Width 900 → 960 mm"]. */
export function describeChange(prev: Design, next: Design, stock?: AnyStock | null, construction: ConstructionProfile = CONSTRUCTION): string[] {
  const out: string[] = [];
  const names = { W: 'Width', H: 'Height', D: 'Depth' } as const;
  for (const k of ['W', 'H', 'D'] as const) {
    if (prev[k] !== next[k]) out.push(`${names[k]} ${fmtMm(prev[k])} → ${fmtMm(next[k])} mm`);
  }
  if (bayCount(prev) !== bayCount(next)) out.push(`Bays ${bayCount(prev)} → ${bayCount(next)}`);
  if ((prev.back ?? 'overlay') !== (next.back ?? 'overlay')) out.push(`Back ${prev.back ?? 'overlay'} → ${next.back ?? 'overlay'}`);
  if ((prev.plinth ?? 0) !== (next.plinth ?? 0)) out.push(next.plinth ? `Plinth ${fmtMm(next.plinth)} mm` : 'Plinth removed');
  if ((prev.joinery ?? 'screwed-butt') !== (next.joinery ?? 'screwed-butt')) out.push(next.joinery === 'housed-dado' ? 'Shelves housed' : 'Shelves screwed');
  if (prev.stockProfileId !== next.stockProfileId) out.push(`Material ${prev.stockProfileId} → ${next.stockProfileId}`);
  if (JSON.stringify(prev.bayWidths ?? null) !== JSON.stringify(next.bayWidths ?? null) && bayCount(prev) === bayCount(next) && prev.W === next.W) out.push('Bay widths changed');
  if (prev.n !== next.n) out.push(`Shelves ${prev.n} → ${next.n}`);
  else if (prev.H === next.H && (prev.plinth ?? 0) === (next.plinth ?? 0) && prev.stockProfileId === next.stockProfileId) {
    const a = derive(prev, stock, construction).shelfDatums;
    const b = derive(next, stock, construction).shelfDatums;
    a.forEach((v, i) => {
      const d = Math.round(((b[i] ?? v) - v) * 10) / 10;
      if (d) out.push(`Shelf ${i + 1} moved ${d > 0 ? 'up' : 'down'} ${fmtNum(Math.abs(d))} mm`);
    });
  } else {
    prev.openings.forEach((g, i) => {
      const ng = next.openings[i];
      if (ng !== g && ng !== undefined) out.push(`Opening ${i + 1} ${fmtMm(g)} → ${fmtMm(ng)} mm`);
    });
  }
  for (let b = 1; b < Math.min(bayCount(prev), bayCount(next)); b++) {
    const lp = layoutOf(prev, b), ln = layoutOf(next, b);
    if (lp.n !== ln.n) out.push(`Bay ${b + 1} shelves ${lp.n} → ${ln.n}`);
    else if (lp.openings.join() !== ln.openings.join() && prev.H === next.H) out.push(`Bay ${b + 1} openings changed`);
  }
  if (!out.length && classifyChange(prev, next, stock, construction) === 'manufacturing') out.push('Construction or stock changed');
  return out;
}

/**
 * A revision label for the working edits since `base` (the current revision's design):
 * the first change, "+N" for more, "Appearance" for look-only edits, else `fallback`.
 */
export function changeLabel(base: Design | null | undefined, design: Design, fallback = 'Customised'): string {
  if (!base) return fallback;
  const ch = describeChange(base, design);
  if (!ch.length) {
    const kind = classifyChange(base, design);
    if (kind === 'appearance') return 'Appearance';
    if (kind === 'layout') return design.even !== base.even ? (design.even ? 'Even on' : 'Even off') : 'Layout';
    return fallback;
  }
  return ch.length > 1 ? `${ch[0]} +${ch.length - 1}` : ch[0]!;
}
